import {
  BarChart as BarChartIcon,
  AddCircle as AddCircleIcon,
  CheckCircle as CheckCircleIcon,
  WarningOutlined,
} from '@mui/icons-material'
import { Box, Container, Divider, Grid, Stack } from '@mui/material'
import type { FC } from 'react'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import DashboardStat from '@/components/DashboardStat'
import ErrorPage from '@/components/UI/ErrorPage'
import AppBarPageContainer from '@/components/UI/PageContainer'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import Tabs from '@/components/UI/Tabs'
import useTab from '@/hooks/useTab'
import useUser from '@/hooks/useUser'
import { getQueueStats } from '@/lib/apollo'
import { isSquad } from '@/utils/squad'
import { defaultGridSpacing } from '@/utils/themes'
import NotFoundPage from '@/views/NotFound'

import AppBarContent from './AppBarContent'
import Assigned from './Assigned'
import Chased from './Chased'
import Escalations from './Escalations'
import Executives from './Executives'
import Over60Hours from './Over60Hours'
import Reopened from './Reopened'
import Unassigned from './Unassigned'

export type Props = {
  collapsedMenu: boolean
}

export type Params = {
  squad: string
}

const tabs = [
  'escalations',
  'chased',
  'reopened',
  'unassigned',
  'assigned',
  'over-60-hours',
  'executives',
] as const

type Tab = (typeof tabs)[number]

const SquadPage: FC<Props> = ({ collapsedMenu }) => {
  const { squad } = useParams<Params>()

  const { customerId } = useUser()

  const { selectedTab, handleChangeTab } = useTab<Tab>({
    availableTabs: tabs,
    defaultTab: 'escalations',
  })

  const [fetch, { data: fetchResult, loading, error }] = getQueueStats.useLazyQuery()

  const squadExists = isSquad(squad)

  useEffect(() => {
    if (!customerId || !squadExists) {
      return
    }

    fetch({
      variables: {
        customerId,
        squad,
      },
    })
  }, [fetch, customerId, squadExists, squad])

  const transformedData = getQueueStats.toTransformedData(fetchResult)

  return !squadExists ? (
    <NotFoundPage />
  ) : error ? (
    <ErrorPage />
  ) : (
    <AppBarPageContainer
      loading={loading || transformedData === undefined}
      isNavigationDrawerOpen={!collapsedMenu}
      headerAppBarContent={<AppBarContent squad={squad} />}
      pageContent={
        loading || transformedData === undefined ? (
          <></>
        ) : (
          <>
            <Container maxWidth="xl">
              <Grid
                container
                paddingTop={defaultGridSpacing}
                spacing={defaultGridSpacing}
              >
                <PaperedGridItem xs={12}>
                  <Stack direction="row" spacing={4}>
                    <DashboardStat
                      label="Inventory"
                      value={transformedData.totalInventoryBySquad[squad]}
                      icon={<BarChartIcon color="info" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Added today"
                      value={transformedData.totalAddedToInventoryBySquad[squad]}
                      icon={<AddCircleIcon color="warning" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Completed today"
                      value={transformedData.totalRemovedFromInventoryBySquad[squad]}
                      secondaryValue={`(${transformedData.totalAutoPassedBySquad[squad]} Auto | ${transformedData.totalRemovedFromInventoryBySMEsBySquad[squad]} SMEs)`}
                      icon={<CheckCircleIcon color="success" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Missed SLA"
                      value={transformedData.totalMissedSLALastWorkingDayBySquad[squad]}
                      secondaryValue={`Last working day`}
                      icon={<WarningOutlined color="warning" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Missed SLA"
                      value={transformedData.totalMissedSLATwoDaysInARowBySquad[squad]}
                      secondaryValue={`Two days in a row`}
                      icon={<WarningOutlined color="warning" />}
                      isPrimaryMetric
                    />
                  </Stack>
                </PaperedGridItem>
                <Grid item xs={12}>
                  <Tabs
                    value={selectedTab}
                    onChange={handleChangeTab}
                    config={[
                      {
                        label: `Escalations (${transformedData.totalEscalated})`,
                        value: 'escalations' as Tab,
                      },
                      {
                        label: `Chased (${transformedData.totalChased})`,
                        value: 'chased' as Tab,
                      },
                      {
                        label: `Reopened (${transformedData.totalReopened})`,
                        value: 'reopened' as Tab,
                      },
                      {
                        label: `Unassigned (${transformedData.totalUnassigned})`,
                        value: 'unassigned' as Tab,
                      },
                      {
                        label: `Assigned (${transformedData.totalAssigned})`,
                        value: 'assigned' as Tab,
                      },
                      {
                        label: `Over 60 hours (${transformedData.totalOver60Hours})`,
                        value: 'over-60-hours' as Tab,
                      },
                      {
                        label: `Executives`,
                        value: 'executives' as Tab,
                      },
                    ]}
                  />
                </Grid>
              </Grid>
            </Container>
            <Box paddingTop={defaultGridSpacing} paddingBottom={defaultGridSpacing}>
              {selectedTab === 'escalations' && (
                <div data-testid="escalations">
                  <Escalations collapsedMenu={collapsedMenu} squad={squad} />
                </div>
              )}

              {selectedTab === 'chased' && (
                <div data-testid="chased">
                  <Chased collapsedMenu={collapsedMenu} squad={squad} />
                </div>
              )}
              {selectedTab === 'reopened' && (
                <div data-testid="reopened">
                  <Reopened collapsedMenu={collapsedMenu} squad={squad} />
                </div>
              )}
              {selectedTab === 'unassigned' && (
                <div data-testid="unassigned">
                  <Unassigned collapsedMenu={collapsedMenu} squad={squad} />
                </div>
              )}
              {selectedTab === 'assigned' && (
                <div data-testid="assigned">
                  <Assigned collapsedMenu={collapsedMenu} squad={squad} />
                </div>
              )}
              {selectedTab === 'over-60-hours' && (
                <div data-testid="over-60-hours">
                  <Over60Hours collapsedMenu={collapsedMenu} squad={squad} />
                </div>
              )}
              {selectedTab === 'executives' && (
                <div data-testid="executives">
                  <Executives collapsedMenu={collapsedMenu} squad={squad} />
                </div>
              )}
            </Box>
          </>
        )
      }
    />
  )
}

export default SquadPage
